import React, { FC, useState } from 'react';

import PropTypes from 'prop-types';

import * as analytics from '@adsk/acs-web-analytics';
import Avatar from '@adsk/alloy-react-avatar';
import { BasicButton } from '@adsk/alloy-react-button';
import { TriangleUpIcon, TriangleDownIcon } from '@adsk/alloy-react-icon';
import theme from '@adsk/alloy-react-theme';
import { Flyout } from '@adsk/alloy-react-tooltip';
import SubscriptionsModal from '@adsk/alloy-subscriptions-modal';

import Profile from './Profile';
import ProfileLoader from './ProfileLoader';
import { ANALYTICS_TYPES } from '../helpers/analytics';
import { userShape } from '../helpers/propTypes';
import { User } from '../helpers/types';

type ProfileButtonProps = {
  showSubscriptionsModal?: boolean;
  user?: User | null;
  projectId?: string;
  onLogout?: () => void;
};

/**
 * Acs ProfileButton
 */
const ProfileButton: FC<ProfileButtonProps> = ({
  showSubscriptionsModal,
  user,
  projectId,
  onLogout,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [isModalOpen, setModalOpen] = useState(false);

  const TriangleIconComponent = menuOpen ? TriangleUpIcon : TriangleDownIcon;

  return user ? (
    <Flyout
      show={menuOpen}
      onHide={() => setMenuOpen(false)}
      onShow={() => setMenuOpen(true)}
      placement={Flyout.PLACEMENTS.BOTTOM_END}
      content={
        <Profile
          user={user}
          projectId={projectId}
          onLogout={onLogout}
          onClose={() => setMenuOpen(false)}
          onOpenSubscriptions={() => setModalOpen(true)}
          showSubscriptionsModal={showSubscriptionsModal}
        />
      }
      offset={[0, 20]}
      containerPadding={0}
      getTargetProps={() => ({
        style: { display: 'flex', alignSelf: 'center' },
      })}
    >
      <BasicButton
        onClick={() => {
          setMenuOpen(!menuOpen);
          analytics.track(ANALYTICS_TYPES.TOPNAVIGATION_USERAREA_CLICK);
        }}
        data-testid="TopNavigation__user"
        style={{
          margin: 0,
          padding: 0,
          color: theme.colors.charcoal900,
          '&:hover': {
            color: theme.colors.charcoal700,
          },
        }}
      >
        <Avatar
          image={user.imageUrl}
          size={Avatar.SIZES.MEDIUM_24}
          name={user.name}
        />
        <TriangleIconComponent
          style={{
            color: theme.colors.charcoal700,
            flexShrink: 0,
            marginLeft: 4,
          }}
          size={20}
        />
      </BasicButton>
      {showSubscriptionsModal && isModalOpen && (
        <SubscriptionsModal onCloseModal={() => setModalOpen(false)} />
      )}
    </Flyout>
  ) : (
    <ProfileLoader />
  );
};

ProfileButton.displayName = 'ProfileButton';

ProfileButton.propTypes = {
  /** show subscriptions modal (page level) */
  showSubscriptionsModal: PropTypes.bool,
  /** current user */
  user: userShape,
  /** current selected project id */
  projectId: PropTypes.string,
  /** callback called on logout */
  onLogout: PropTypes.func,
};

export default ProfileButton;
